import { WEATHER, type WeatherLayerDef } from '../constants';

export type WeatherTimeMode = 'latest' | 'timeline';
export interface WeatherMetadata {
  times: string;
  defaultTime: string | null;
  legendUrl: string | null;
}
export interface WeatherRequest {
  key: string;
  time?: string;
  revision: number;
}

/** Only advertised dates are selectable. A missing interval is a real data gap. */
export function resolveWeatherDate(
  metadata: WeatherMetadata,
  mode: WeatherTimeMode,
  day: string,
  nowMs = Date.now(),
): string | null {
  const today = new Date(nowMs).toISOString().slice(0, 10);
  const target = mode === 'latest' ? today : day;
  if (target > today) return null;
  const candidates: string[] = [];
  for (const extent of metadata.times.split(',')) {
    const [startRaw, endRaw, period = 'P1D'] = extent.trim().split('/');
    const start = startRaw?.slice(0, 10);
    const end = endRaw?.slice(0, 10) ?? start;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(start ?? '') || !end || start > target) continue;
    if (mode === 'timeline' && target > end) continue;
    const limit = target < end ? target : end;
    let candidate: string;
    const days = /^P(\d+)D$/.exec(period);
    const months = /^P(\d+)(M|Y)$/.exec(period);
    if (days) {
      const step = Math.max(1, Number(days[1])) * 86_400_000;
      candidate = new Date(
        Date.parse(start) + Math.floor((Date.parse(limit) - Date.parse(start)) / step) * step,
      )
        .toISOString()
        .slice(0, 10);
    } else if (months) {
      const first = new Date(start);
      const last = new Date(limit);
      const step = Math.max(1, Number(months[1])) * (months[2] === 'Y' ? 12 : 1);
      const difference =
        (last.getUTCFullYear() - first.getUTCFullYear()) * 12 + last.getUTCMonth() - first.getUTCMonth();
      first.setUTCMonth(first.getUTCMonth() + Math.floor(difference / step) * step);
      if (first > last) first.setUTCMonth(first.getUTCMonth() - step);
      candidate = first.toISOString().slice(0, 10);
    } else continue;
    if (candidate <= target) candidates.push(candidate);
  }
  return candidates.sort().at(-1) ?? null;
}

export function parseWeatherMetadata(xml: string, layerName: string): WeatherMetadata {
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  if (doc.querySelector('parsererror')) throw new Error('The provider returned invalid layer information.');
  const layer = [...doc.getElementsByTagName('Layer')].find((el) =>
    [...el.children].some((child) => child.localName === 'Name' && child.textContent === layerName),
  );
  if (!layer) throw new Error('This layer is not currently advertised by its provider.');
  const children = [...layer.children];
  const time =
    children.find((el) => el.localName === 'Extent' && el.getAttribute('name') === 'time') ??
    children.find((el) => el.localName === 'Dimension' && el.getAttribute('name') === 'time');
  const resource = layer.querySelector('LegendURL OnlineResource');
  const legend = resource?.getAttributeNS('http://www.w3.org/1999/xlink', 'href') ?? null;
  // Metadata may link anywhere; use only the already permitted imagery hosts.
  const legendUrl =
    legend && /^https:\/\/(gibs\.earthdata\.nasa\.gov|mesonet\.agron\.iastate\.edu)\//.test(legend)
      ? legend
      : null;
  return {
    times: time?.textContent?.trim() ?? '',
    defaultTime: time?.getAttribute('default') ?? null,
    legendUrl,
  };
}

const cache = new Map<string, { at: number; promise: Promise<string> }>();
export async function fetchWeatherMetadata(config: WeatherLayerDef, force = false): Promise<WeatherMetadata> {
  let entry = cache.get(config.url);
  if (!entry || force || Date.now() - entry.at >= WEATHER.REFRESH_INTERVAL_MS) {
    const promise = fetch(`${config.url}?service=WMS&request=GetCapabilities&version=1.1.1`, {
      signal: AbortSignal.timeout(20_000),
      cache: 'no-cache',
    }).then(async (response) => {
      if (!response.ok) throw new Error(`Layer information is unavailable (HTTP ${response.status}).`);
      return response.text();
    });
    entry = { at: Date.now(), promise };
    cache.set(config.url, entry);
    void promise.catch(() => {
      if (cache.get(config.url)?.promise === promise) cache.delete(config.url);
    });
  }
  return parseWeatherMetadata(await entry.promise, config.layers);
}

export function weatherSource(key: string): string {
  return WEATHER.LAYERS[key]?.gibs ? 'NASA GIBS · daily observations' : 'NOAA via IEM · latest observations';
}

export const WEATHER_NOTES: Record<string, string> = {
  viirs_true:
    'Natural-colour daily mosaic. White clouds; gaps can occur while the latest day is being assembled.',
  modis_terra: 'Natural-colour daily mosaic from Terra. White clouds; coverage depends on overpasses.',
  modis_aqua: 'Natural-colour daily mosaic from Aqua. White clouds; coverage depends on overpasses.',
  goes_ir:
    'Infrared cloud imagery over North America. Enhanced colours distinguish colder cloud tops; this is not a rainfall map.',
  goes_vis: 'Visible-light cloud imagery over North America. Night-time areas are dark.',
  goes_wv:
    'Water-vapour imagery over North America shows atmospheric moisture patterns, not surface rainfall.',
  nexrad:
    'Radar reflectivity over the contiguous US. Clear areas may have no echoes or no coverage. Reflectivity is not a direct rainfall-rate measurement.',
};
